import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { api } from '../api';
import { Aviso, Etiqueta, Painel, data, useAcao, type TomEtiqueta } from '../comum';

/**
 * A fila de trabalho de fundo.
 *
 * 🔴 **Esta tela existe para responder "por que isso não rodou?".** Era a
 * pergunta sem resposta: o produto tinha sete laços de `setTimeout` executando
 * em silêncio, sem registro de tentativa, de falha ou de espera. Quando o
 * cliente dizia "o inventário não atualizou", não havia onde olhar — só
 * reiniciar e torcer.
 *
 * Por isso a tela mostra `tentativas`, `erro` e `executarEm` com destaque: um
 * job que falhou duas vezes e volta daqui a oito minutos é uma informação
 * completamente diferente de um job travado, e antes as duas apareciam iguais
 * (isto é, não apareciam).
 */

interface Job {
  readonly id: string;
  readonly tipo: string;
  readonly status: 'PENDING' | 'RUNNING' | 'SUCCESS' | 'FAILED' | 'CANCELED';
  readonly organizationId: number | null;
  readonly tentativas: number;
  readonly maxTentativas: number;
  readonly executarEm: string;
  readonly criadoEm: string;
  readonly iniciadoEm: string | null;
  readonly concluidoEm: string | null;
  readonly leasePor: string | null;
  readonly erro: string | null;
  readonly resultado: unknown;
}

type Resumo = Readonly<Record<Job['status'], number>>;

const ROTULO_TIPO: Readonly<Record<string, string>> = {
  'verification.processar': 'Verificar ações executadas',
  'monitor.avaliar': 'Avaliar monitores',
  'sync.executar': 'Sincronizar com o KACE',
  'notificacao.reprocessar': 'Reenviar notificações',
  'jobs.podar': 'Limpar histórico da fila',
};

const ESTADO: Readonly<Record<string, { rotulo: string; tom: TomEtiqueta; explica: string }>> = {
  PENDING: {
    rotulo: 'Na fila',
    tom: 'alerta',
    explica: 'Aguardando um worker. Se houver tentativas, já falhou antes e está no backoff.',
  },
  RUNNING: {
    rotulo: 'Executando',
    tom: 'neutro',
    explica: 'Reservado por um worker. Se o processo cair, volta para a fila sozinho.',
  },
  SUCCESS: { rotulo: 'Concluído', tom: 'ok', explica: 'Terminou sem erro.' },
  FAILED: {
    rotulo: 'Falhou',
    tom: 'erro',
    explica: 'Esgotou as tentativas, ou o erro era do tipo que retentar não conserta.',
  },
  CANCELED: {
    rotulo: 'Cancelado',
    tom: 'neutro',
    explica: 'Cancelado antes de começar.',
  },
};

const DESCONHECIDO = {
  rotulo: 'Estado não reconhecido',
  tom: 'neutro' as TomEtiqueta,
  explica: 'O servidor devolveu um estado que esta versão da tela não conhece.',
};

export function FilaDeTrabalho({
  aoPerderSessao,
  podeDisparar,
}: {
  aoPerderSessao: () => void;
  podeDisparar: boolean;
}): ReactNode {
  const acao = useAcao(aoPerderSessao);
  const [itens, setItens] = useState<readonly Job[] | null>(null);
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [soProblemas, setSoProblemas] = useState(false);

  const carregar = useCallback(async (): Promise<void> => {
    try {
      const params = new URLSearchParams({ limite: '50' });
      if (soProblemas) params.set('status', 'PENDING,RUNNING,FAILED');
      const r = await api.get<{ itens: readonly Job[]; resumo: Resumo }>(
        `/api/jobs?${params.toString()}`,
      );
      setItens(r.itens);
      setResumo(r.resumo);
    } catch (e: unknown) {
      if ((e as { precisaLogin?: boolean }).precisaLogin === true) aoPerderSessao();
      else setItens([]);
    }
  }, [aoPerderSessao, soProblemas]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /**
   * Enquanto houver trabalho em aberto, a tela volta a perguntar.
   *
   * O worker roda no servidor, não nesta aba. Sem o recarregamento, quem
   * disparou um sync ficaria olhando "Na fila" até apertar F5 — e concluiria
   * pelo silêncio que travou. É justamente a conclusão errada que esta tela
   * existe para evitar.
   */
  const emAberto = (itens ?? []).some(
    (j) => j.status === 'PENDING' || j.status === 'RUNNING',
  );
  useEffect(() => {
    if (!emAberto) return;
    const t = setInterval(() => void carregar(), 10_000);
    return () => clearInterval(t);
  }, [emAberto, carregar]);

  const dispararSync = (): void => {
    void acao.rodar(async () => {
      const r = await api.post<{ jaExistia: boolean }>('/api/jobs', {
        tipo: 'sync.executar',
      });
      await carregar();
      // A frase distingue os dois casos de propósito: "já estava na fila" não é
      // erro, e dizer "enfileirado" nesse caso faria a pessoa clicar de novo.
      return r.jaExistia
        ? 'Uma sincronização já estava na fila. Nada foi duplicado.'
        : 'Sincronização enfileirada.';
    });
  };

  const cancelar = (j: Job): void => {
    void acao.rodar(async () => {
      await api.post(`/api/jobs/${encodeURIComponent(j.id)}/cancelar`, {
        motivo: 'cancelado pela tela',
      });
      await carregar();
      return 'Job cancelado.';
    });
  };

  return (
    <Painel
      titulo="Fila de trabalho"
      sub="O que roda sozinho: verificação de ações, monitores, notificações e sincronização."
    >
      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
      {acao.ok !== null && <Aviso tipo="ok">{acao.ok}</Aviso>}

      {resumo !== null && (
        <p className="suave">
          {resumo.PENDING} na fila · {resumo.RUNNING} executando · {resumo.SUCCESS}{' '}
          concluídos · {resumo.FAILED} com falha · {resumo.CANCELED} cancelados
        </p>
      )}

      <div className="linha" style={{ marginBottom: 12 }}>
        <label className="suave">
          <input
            type="checkbox"
            checked={soProblemas}
            onChange={(e) => setSoProblemas(e.target.checked)}
          />{' '}
          Só o que não terminou bem
        </label>
        {podeDisparar && (
          <button className="encolhe" disabled={acao.executando} onClick={dispararSync}>
            Sincronizar agora
          </button>
        )}
      </div>

      {itens !== null && itens.length === 0 && (
        <p className="suave">Nada na fila.</p>
      )}

      {(itens ?? []).map((j) => {
        const estado = ESTADO[j.status] ?? DESCONHECIDO;
        const esperando = j.status === 'PENDING' && j.tentativas > 0;
        return (
          <div
            key={j.id}
            style={{ borderBottom: '1px solid var(--borda)', paddingBottom: 12, marginBottom: 12 }}
          >
            <div className="linha">
              <div>
                <strong>{ROTULO_TIPO[j.tipo] ?? j.tipo}</strong>{' '}
                <span className="suave">
                  · criado {data(j.criadoEm)}
                  {j.organizationId !== null && ` · organização ${j.organizationId}`}
                </span>
              </div>
              <Etiqueta tom={estado.tom} titulo={estado.explica}>
                {estado.rotulo}
              </Etiqueta>
            </div>

            {/*
              🔴 Tentativas e próxima execução ficam visíveis mesmo no caminho
              feliz. "Falhou duas vezes e volta às 14h32" é a informação que
              transforma um chamado de "está travado" numa espera consciente.
            */}
            {(j.tentativas > 0 || j.status === 'RUNNING') && (
              <p className="suave">
                Tentativa {j.tentativas} de {j.maxTentativas}
                {j.leasePor !== null && ` · worker ${j.leasePor}`}
                {esperando && ` · próxima tentativa ${data(j.executarEm)}`}
              </p>
            )}

            {j.erro !== null && (
              <p className={j.status === 'FAILED' ? 'erro' : 'suave'}>{j.erro}</p>
            )}

            {j.resultado != null && (
              <details>
                <summary className="suave">Resultado</summary>
                <pre className="mono">{JSON.stringify(j.resultado, null, 2)}</pre>
              </details>
            )}

            {/* Só PENDING pode ser cancelado — o servidor recusa o resto, e a
                tela não deve oferecer um botão que vai dar 409. */}
            {j.status === 'PENDING' && podeDisparar && (
              <button
                className="encolhe"
                disabled={acao.executando}
                onClick={() => cancelar(j)}
              >
                Cancelar
              </button>
            )}
          </div>
        );
      })}
    </Painel>
  );
}
